const {
    clone, concat, difference, each, filter, last, replace, remove, size, some, trim, find, isUndefined,
} = require('lodash');

const PAYMENT_MANAGE_MODE_STATE_ID = 'gcDisableState';
const GC_CONDENSED_VIEW_STATE = 'gcDisableCondensedView:';
const GC_SELECT_CARD_AMOUNT_KEY = 'gcSelectCardAmount';
const GC_SELECT_CARD_WITH_NO_AMOUNT_SET_DISABLE_STATE = 'gcSelectCardWithNoAmountSet';

class GiftCardController {
    constructor($q, $filter, moveFocus, digitalPayPaymentErrorDisplayService, digitalPayPaymentDistributionService, digitalPayPaymentInstrumentsService, settings, dateHelperService, digitalPayErrorMessageGenerator) {
        this.$q = $q;
        this.$filter = $filter;
        this.moveFocus = moveFocus;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.digitalPayPaymentDistributionService = digitalPayPaymentDistributionService;
        this.digitalPayPaymentInstrumentsService = digitalPayPaymentInstrumentsService;
        this.settings = settings;
        this.dateHelperService = dateHelperService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
    }

    $onInit() {
        this.giftCardsInErrorState = {};
        this.giftCardsEnabled = false;
        this.manageMode = false;
        this.newGiftCards = [];
        this.deleteMessage = this.settings.digitalPayMessages.giftCardDeleteMessage;
        this.gcWarningMessage = this.unavailableCards && this.unavailableCards.length
            ? this.settings.digitalPayError.giftCardBalanceApiWarningMessage
            : '';

        if (this.hasValidCards() && this.shouldSelectGiftCard) {
            this.selectGiftCard(this.validGiftCards[0]);
        }

        this.giftCardBalanceIsValid = true;
    }

    initializeCards() {
        this.unavailableCards = filter(this.giftCards, giftCard => this.isCardUnavailable(giftCard));
        const availableCards = difference(this.giftCards, this.unavailableCards);
        this.problemGiftCards = filter(availableCards, giftCard => this.isProblemGiftCard(giftCard));
        this.validGiftCards = concat(difference(availableCards, this.problemGiftCards), this.unavailableCards);
    }

    getGiftCardError() {
        const expiredCards = filter(this.problemGiftCards, giftCard => giftCard.Expired);
        const zeroBalanceCards = filter(this.problemGiftCards, giftCard => !giftCard.Expired && giftCard.Balance === 0.0);

        const expiredMsg =
            this.getIndividualErrorMessage(expiredCards, this.settings.digitalPayError.giftCardExpiredMessage, this.settings.digitalPayError.giftCardMultipleExpiredErrorMessage);

        const zeroBalanceMsg =
            this.getIndividualErrorMessage(zeroBalanceCards, this.settings.digitalPayError.giftCardInsufficientFundsMessage, this.settings.digitalPayError.giftCardMultipleInsufficientFundsErrorMessage);

        if (expiredMsg && zeroBalanceMsg) {
            return this.digitalPayErrorMessageGenerator.formatErrorMessage(this.settings.digitalPayError.giftCardMultipleErroredCardsMessage, [expiredMsg, zeroBalanceMsg]);
        }
        if (expiredMsg) {
            return expiredMsg;
        }
        if (zeroBalanceMsg) {
            return zeroBalanceMsg;
        }
        return '';
    }

    getIndividualErrorMessage(list, singularError, pluralisedError) {
        if (size(list) === 1) {
            return singularError;
        } else if (size(list) > 1) {
            return pluralisedError;
        }
        return '';
    }

    toggleGiftCards() {
        this.giftCardsEnabled = !this.giftCardsEnabled;
        if (this.giftCardsEnabled) {
            // On toggle on, add a new card
            this.addNewGiftCard();
        } else {
            // On toggle off, destroy all new gift cards
            each(this.newGiftCards, (giftCard) => {
                this.digitalPayPaymentDistributionService.removePayment(giftCard.NewStagedCardId);
                this.prePaymentActionDefinitions.removeAction(giftCard.NewStagedCardId);
                this.onChangeSelectGiftCard({ id: giftCard.PaymentInstrumentId || giftCard.NewStagedCardId, isSelected: false });
            });
            this.newGiftCards = [];
        }
    }

    toggleManage() {
        this.manageMode = !this.manageMode;
        this.onInstrumentTypeInManageMode({
            type: this.manageMode ? this.settings.digitalPayPaymentTypes.giftCardType : this.settings.digitalPayPaymentTypes.emptyType,
        });
        this.setStateDefinitions(this.manageMode);

        if (this.manageMode) {
            const lastAddedGiftCard = last(this.newGiftCards);
            if (lastAddedGiftCard && !lastAddedGiftCard.IsCondensed) {
                this.removeGiftCardFromUi(lastAddedGiftCard);
            }
            this.moveFocus('#digitalPayGiftCard');
        }
    }

    addNewGiftCard() {
        // Set all existing new gift cards to condensed view
        each(this.newGiftCards, (giftCard) => {
            giftCard.IsCondensed = true;
            if (!giftCard.Amount) {
                this.paymentStateDefinitions.addDisablePaymentState(
                    `${GC_CONDENSED_VIEW_STATE}${giftCard.NewStagedCardId}`,
                    replace(this.settings.digitalPayScreenReaderMessages.giftCardAmountDisableMessage, '{0}', giftCard.CardSuffix),
                );
            }
        });

        // Increment the last new gift card's id
        // Cannot use length as the user may have deleted an item which will result in duplicate ids
        const newId = this.hasNewCards()
            ? parseInt(trim(last(this.newGiftCards).NewStagedCardId, this.paymentAddCardAmountKey)) + 1
            : 0;

        const newGiftCard = {
            NewStagedCardId: `${this.paymentAddCardAmountKey}${newId}`,
            CardNumber: undefined,
            PinCode: undefined,
            Balance: undefined,
            Amount: undefined,
            IsCondensed: false,
        };
        this.newGiftCards.push(newGiftCard);
        this.refreshUsedCardNumbersList();
    }

    /**
     * Gets a new list of new gift card numbers
     */
    refreshUsedCardNumbersList() {
        this.usedCardNumbers = filter(this.newGiftCards, card => card.CardNumber).map(card => card.CardNumber);
    }

    /**
     * Select a Saved GiftCard.
     * @param {object} giftCard
     */
    selectGiftCard(giftCard) {
        // Do not allow selecting when in manage mode, $0 balance, expired cards or newly created cards
        if ((this.manageMode || this.isProblemGiftCard(giftCard) || !giftCard.PaymentInstrumentId) && !this.isCardUnavailable(giftCard)) {
            return;
        }

        giftCard.Selected = !giftCard.Selected;

        const giftCardKey = this.getGiftCardKey(giftCard);
        const giftCardSelectDisableStateKey = this.getGiftCardSelectDisableStateKey(giftCard);

        if (!giftCard.Selected) {
            giftCard.Amount = undefined;
            this.digitalPayPaymentDistributionService.removePayment(giftCardKey);
            this.paymentStateDefinitions.removeDisablePaymentState(giftCardSelectDisableStateKey);
            this.prePaymentActionDefinitions.removeAction(giftCardKey);
        } else {
            this.paymentStateDefinitions.addDisablePaymentState(
                giftCardSelectDisableStateKey,
                replace(this.settings.digitalPayScreenReaderMessages.giftCardAmountDisableMessage, '{0}', giftCard.CardSuffix),
            );
        }

        this.onChangeSelectGiftCard({ id: giftCard.PaymentInstrumentId || giftCard.NewStagedCardId, isSelected: giftCard.Selected });
    }

    promptRemoveGiftCard(giftCard) {
        this.confirmationDialog.show().then((dialogResponse) => {
            if (!dialogResponse.success) return;

            if (giftCard.PaymentInstrumentId && isUndefined(giftCard.TokenizedWithSave)) {
                this.removeGiftCard(giftCard);
            } else {
                this.removeGiftCardFromUi(giftCard);
            }
        });
    }

    /**
     * Remove a saved GiftCard from container and the UI.
     * @param {object} giftCard
     * @returns {Promise} promise that returns true if successful
     */
    removeGiftCard(giftCard) {
        return this.digitalPayPaymentInstrumentsService.deleteDigitalPayInstrument({ paymentInstrumentId: giftCard.PaymentInstrumentId }).then(((response) => {
            if (response.Success) {
                this.removeGiftCardFromUi(giftCard);
                this.refreshGiftCards();
            } else {
                this.digitalPayPaymentErrorDisplayService.pushGlobalError(this.settings.digitalPayError.giftCardDeleteErrorMessage);
            }
        }));
    }

    /**
     * Remove a new or saved GiftCard from the UI. Does not remove the card instrument from the container.
     * @param {object} giftCard
     */
    removeGiftCardFromUi(giftCard) {
        const giftCardSelectDisableStateKey = this.getGiftCardSelectDisableStateKey(giftCard);
        const giftCardKey = giftCard.NewStagedCardId ? giftCard.NewStagedCardId : this.getGiftCardKey(giftCard);

        // Remove staged/condensed or newly added giftcard which has been tokenized
        this.digitalPayPaymentDistributionService.removePayment(giftCardKey);
        this.prePaymentActionDefinitions.removeAction(giftCardKey);
        if (giftCard.NewStagedCardId) {
            remove(this.newGiftCards, { NewStagedCardId: giftCard.NewStagedCardId });
            this.refreshUsedCardNumbersList();
        } else if (this.isProblemGiftCard(giftCard)) {
            remove(this.problemGiftCards, { PaymentInstrumentId: giftCard.PaymentInstrumentId });
            remove(this.giftCards, { PaymentInstrumentId: giftCard.PaymentInstrumentId });
        } else {
            remove(this.validGiftCards, { PaymentInstrumentId: giftCard.PaymentInstrumentId });
            remove(this.giftCards, { PaymentInstrumentId: giftCard.PaymentInstrumentId });
        }

        this.paymentStateDefinitions.removeDisablePaymentState(giftCardSelectDisableStateKey);
        this.onChangeSelectGiftCard({ id: giftCard.PaymentInstrumentId || giftCard.NewStagedCardId, isSelected: false });

        if (this.manageMode && !this.hasSavedCards()) {
            this.setStateDefinitions(false);
            this.onInstrumentTypeInManageMode({
                type: this.settings.digitalPayPaymentTypes.emptyType,
            });
            this.manageMode = false;
            if (this.hasNewCards()) {
                this.giftCardsEnabled = !this.giftCardsEnabled;
            }
        }
    }

    setStateDefinitions(isInManageMode) {
        if (isInManageMode) {
            this.paymentStateDefinitions.addDisablePaymentState(PAYMENT_MANAGE_MODE_STATE_ID, this.settings.digitalPayScreenReaderMessages.giftCardManageModeDisableMessage);
        } else {
            this.paymentStateDefinitions.removeDisablePaymentState(PAYMENT_MANAGE_MODE_STATE_ID);
        }
    }

    /**
     * Notifies when value in any saved GiftCard amount fields changes.
     * @param {number} amount
     * @param {object} card
     */
    notifyChangeAmount(amount, card) {
        const giftCardKey = this.getGiftCardKey(card);
        const giftCardSelectDisableStateKey = this.getGiftCardSelectDisableStateKey(card);

        if (!amount || amount <= 0) {
            this.digitalPayPaymentDistributionService.removePayment(giftCardKey);
            this.prePaymentActionDefinitions.removeAction(giftCardKey);
            if (card.PaymentInstrumentId) {
                this.paymentStateDefinitions.addDisablePaymentState(giftCardSelectDisableStateKey, replace(this.settings.digitalPayScreenReaderMessages.giftCardAmountDisableMessage, '{0}', card.CardSuffix));
            }
            if (card.IsCondensed) {
                this.paymentStateDefinitions.addDisablePaymentState(`${GC_CONDENSED_VIEW_STATE}${card.NewStagedCardId}`, replace(this.settings.digitalPayScreenReaderMessages.giftCardAmountDisableMessage, '{0}', card.CardSuffix));
            }
        } else {
            this.digitalPayPaymentDistributionService.addPayment(giftCardKey, amount);
            this.paymentStateDefinitions.removeDisablePaymentState(giftCardSelectDisableStateKey);
            if (card.PaymentInstrumentId) {
                this.prePaymentActionDefinitions.addAction(giftCardKey, () => this.$q.when({
                    paymentInstrumentId: card.PaymentInstrumentId,
                    amount,
                }));
            } else {
                this.prePaymentActionDefinitions.addAction(giftCardKey, this.tokenizeGiftCard.bind(this, card));
            }
            if (card.IsCondensed) {
                this.paymentStateDefinitions.removeDisablePaymentState(GC_CONDENSED_VIEW_STATE + card.NewStagedCardId);
            }
        }
    }

    notifyFormValidityChange(isValid) {
        this.giftCardBalanceIsValid = isValid;
    }
    /**
     * Tokenize new GiftCards.
     * @param {object} giftCard
     */
    tokenizeGiftCard(giftCard) {
        return this.digitalPayPaymentInstrumentsService.tokenizeGiftCard(giftCard.CardNumber, giftCard.Pin, giftCard.IsSavingCard)
            .then((response) => {
                if (response.Success) {

                    // Hydrate NewGiftCard with newly available data.
                    // By reference the object in the newGiftCards list is also updated.
                    giftCard.PaymentInstrumentId = response.GiftCard.PaymentInstrumentId;
                    giftCard.CardSuffix = response.GiftCard.CardSuffix;
                    giftCard.TokenizedWithSave = giftCard.IsSavingCard;

                    if (this.hasPrePaymentActionFailures && some(this.newGiftCards, newGiftCard => newGiftCard.CardNumber === giftCard.CardNumber)) {
                        this.removeGiftCard(giftCard);
                        this.addNewGiftCard();
                    }

                    if (last(this.newGiftCards) === giftCard) {
                        this.addNewGiftCard();
                    }

                    return {
                        paymentInstrumentId: response.GiftCard.PaymentInstrumentId,
                        amount: this.digitalPayPaymentDistributionService.getPayment(giftCard.NewStagedCardId),
                    };
                }

                // Payment error is returned here instead of tokenization error because tokenization happens at the time of payment, which has higher precedence
                const errorMessage = this.digitalPayErrorMessageGenerator.getGiftCardPaymentError(response.ErrorCode, giftCard.CardNumber.slice(-4), response.ErrorMessage);
                return this.$q.reject({ errorMessage });
            });
    }

    /**
     * Set true/false for the error state of a giftcard.
     * @param {string} paymentInstrumentId
     * @param {Object} state
     */
    setSavedCardErrorState(paymentInstrumentId, state) {
        if (state) {
            this.giftCardsInErrorState[paymentInstrumentId] = state;
        } else {
            delete this.giftCardsInErrorState[paymentInstrumentId];
        }
    }

    clearSavedCardErrorStates() {
        this.giftCardsInErrorState = {};
    }

    clearNewlyAddedGiftCards() {
        const cardsToRemove = clone(this.newGiftCards);
        each(cardsToRemove, (cardToRemove) => {
            const isTokenized = newGiftCard => newGiftCard.PaymentInstrumentId;
            if (cardToRemove.IsSavingCard && isTokenized(cardToRemove) && !this.isSavedCard(cardToRemove.PaymentInstrumentId)) {
                this.removeGiftCard(cardToRemove);
            } else {
                this.removeGiftCardFromUi(cardToRemove);
            }
        });

        // If a payment error occurred if the payment contained a new gift card, we want to show the user an empty add card form
        this.addNewGiftCard();
    }

    isSavedCard(paymentInstrumentId) {
        return some(this.validGiftCards, card => card.PaymentInstrumentId === paymentInstrumentId)
            || some(this.problemGiftCards, card => card.PaymentInstrumentId === paymentInstrumentId);
    }

    /**
     * Determine whether the saved giftcard with the given id is in error state.
     * @param {string} paymentInstrumentId
     */
    isCardInErrorState(paymentInstrumentId) {
        return !isUndefined(this.giftCardsInErrorState[paymentInstrumentId]);
    }

    getGiftCardKey(card) {
        return card.PaymentInstrumentId
            ? `${GC_SELECT_CARD_AMOUNT_KEY}${card.PaymentInstrumentId}`
            : card.NewStagedCardId;
    }

    getGiftCardSelectDisableStateKey(card) {
        return `${GC_SELECT_CARD_WITH_NO_AMOUNT_SET_DISABLE_STATE}${card.PaymentInstrumentId
            ? card.PaymentInstrumentId
            : card.NewStagedCardId}`;
    }

    showAddNewGiftCard() {
        const newestGiftCard = last(this.newGiftCards);

        return (this.giftCards && this.hasSavedCards() || this.hasNewCards())
            && !this.manageMode
            && this.digitalPayPaymentDistributionService.getRemainingAmount() > 0
            && (!newestGiftCard || (newestGiftCard.CardNumber && newestGiftCard.Pin && newestGiftCard.Amount && !newestGiftCard.ErrorCode))
            && this.giftCardBalanceIsValid;
    }

    showSavedGiftCards() {
        return this.hasValidCards();
    }

    showNewGiftCards() {
        return this.hasNewCards() || (this.giftCardsEnabled && this.hasSavedCards());
    }

    showActions() {
        return this.showAddNewGiftCard() || (this.hasValidCards() && !this.manageMode) || this.manageMode;
    }

    hasValidCards() {
        return this.validGiftCards && this.validGiftCards.length;
    }

    hasSavedCards() {
        return this.giftCards && this.giftCards.length;
    }

    hasNewCards() {
        return this.newGiftCards && this.newGiftCards.length;
    }

    isCardUnavailable(giftCard) {
        return giftCard.ErrorCode === this.settings.digitalPayErrorCodes.giftCardKnownBalanceCheckFailed ||
            giftCard.ErrorCode === this.settings.digitalPayErrorCodes.giftCardUnknownBalanceCheckFailed ||
            giftCard.ErrorCode === this.settings.digitalPayErrorCodes.giftCardBalanceApiUnavailable;
    }

    isProblemGiftCard(giftCard) {
        return giftCard.Expired || giftCard.Balance === 0.0;
    }

    checkIfDatesAreEqual(date1, date2) {
        return this.dateHelperService.getDateFromEpochInSeconds(date1) === this.dateHelperService.getDateFromEpochInSeconds(date2);
    }

    handleSavedCardPaymentFailure(savedGiftCard, gcErrorState) {
        // Deselect selected GiftCard
        this.selectGiftCard(savedGiftCard);
        this.setSavedCardErrorState(savedGiftCard.PaymentInstrumentId, gcErrorState);
        if (gcErrorState.state.ErrorMessage) {
            this.handleGiftCardPaymentFailure(savedGiftCard, gcErrorState);
        }
    }

    handleGiftCardPaymentFailure(giftCard, gcErrorState) {
        const errorMessage = this.digitalPayErrorMessageGenerator.getGiftCardPaymentError(gcErrorState.state.ErrorCode, giftCard.CardSuffix, gcErrorState.state.ErrorMessage);
        this.digitalPayPaymentErrorDisplayService.pushToPaymentErrorBreakdown(errorMessage);
    }

    $onChanges(changes) {
        // Core logic for changes in the gift cards list, we will refresh the error that is initialized
        if (changes.giftCards && changes.giftCards.currentValue) {
            this.initializeCards();
            this.giftCardError = this.getGiftCardError();
        }

        const currentPaymentErrorStates = changes.paymentErrorStates && changes.paymentErrorStates.currentValue || [];
        if (currentPaymentErrorStates.length) {
            // Filter all giftcard errors.
            const giftCardErrorStates = filter(currentPaymentErrorStates, (value) => {
                const state = value.state;
                return state.ErrorCode && state.PaymentInstrumentType === this.settings.digitalPayPaymentTypes.giftCardType;
            });

            // Clear existing error states.
            this.clearSavedCardErrorStates();

            // Handle all giftcard payment errors.
            each(giftCardErrorStates, (gcErrorState) => {
                const paymentInstrumentId = gcErrorState.state.PaymentInstrumentId;

                // Find selected Giftcard matching the payment error.
                const savedGiftCard = find(this.validGiftCards, c => c.PaymentInstrumentId === paymentInstrumentId && c.Selected);
                const newlyAddedGiftCard = find(this.newGiftCards, c => c.PaymentInstrumentId === paymentInstrumentId);

                if (savedGiftCard) {
                    this.handleSavedCardPaymentFailure(savedGiftCard, gcErrorState);
                } else if (newlyAddedGiftCard && gcErrorState.state.ErrorMessage) {
                    this.handleGiftCardPaymentFailure(newlyAddedGiftCard, gcErrorState);
                }
            });

            if (this.hasNewCards()) {
                this.clearNewlyAddedGiftCards();
            }
        } else if (changes.paymentErrorStates) {
            // Clear existing error states.
            this.clearSavedCardErrorStates();
        }

        if (this.hasNewCards()
            && ((changes.hasPrePaymentActionFailures && changes.hasPrePaymentActionFailures.currentValue)
                || (changes.hasPrePaymentValidationFailure && changes.hasPrePaymentValidationFailure.currentValue)
                || (changes.shouldRemoveNewlyAddedInstruments && changes.shouldRemoveNewlyAddedInstruments.currentValue))) {
            this.clearNewlyAddedGiftCards();
        }
    }
}

GiftCardController.$inject = ['$q', '$filter', 'MoveFocus', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayPaymentDistributionService', 'DigitalPayPaymentInstrumentsService', 'Settings', 'DateHelperService', 'DigitalPayErrorMessageGenerator'];

const giftCardComponentDefinition = {
    controller: GiftCardController,
    template: require('./gift-card.component.html').trim(),
    bindings: {
        giftCards: '<',
        allowedBins: '<',
        paymentAddCardAmountKey: '<',
        paymentStateDefinitions: '<',
        prePaymentActionDefinitions: '<',
        refreshGiftCards: '&',
        setSingleSelectType: '&',
        shouldSelectGiftCard: '<',
        singleSelectType: '<',
        onInstrumentTypeInManageMode: '&',
        onChangeSelectGiftCard: '&',
        paymentErrorStates: '<',
        hasPrePaymentActionFailures: '<',
        hasPrePaymentValidationFailure: '<',
        isImpersonated: '<',
        shouldRemoveNewlyAddedInstruments: '<',
        shouldShowPayingAmount: '<',
    },
};

module.exports = {
    GiftCardController,
    giftCardComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/giftcard/gift-card.component.js